import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import API from "../../api";
import { formatAddressLines, hasAnyAddress } from "../../components/common/address/addressValidators";
import { formatDate, formatDateTime } from "../../utils/date";
import PageBackSection from "../../components/layout/PageBackSection";
import PageHeaderSection from "../../components/layout/PageHeaderSection";
import PageLoadingShell from "../../components/layout/PageLoadingShell";
import Badge from "../../components/common/Badge";
import { getActiveRole } from "../../utils/auth";
import { userEditPath, userListPath } from "../../utils/userRoutes";
import GuardiansSection from "../../components/guardians/GuardiansSection";
import { apiErrorMessage } from '../../utils/apiError';

const ROLE_COLORS = {
    ADMIN: "bg-gradient-to-r from-red-500 to-red-600 text-white",
    MANAGER: "bg-gradient-to-r from-primary-500 to-primary-600 text-white",
    COACH: "bg-gradient-to-r from-warning-500 to-warning-600 text-white",
    STUDENT: "bg-gradient-to-r from-success-500 to-success-600 text-white",
};

const ROLE_ICONS = {
    ADMIN: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
        </svg>
    ),
    MANAGER: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
        </svg>
    ),
    COACH: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
        </svg>
    ),
    STUDENT: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
        </svg>
    ),
};

const InfoCard = ({ title, icon, children, className = "" }) => (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow ${className}`}>
        <div className="flex items-center gap-2 mb-4">
            {icon && <div className="text-primary-600">{icon}</div>}
            <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        </div>
        <div className="space-y-3">{children}</div>
    </div>
);

const InfoField = ({ icon, label, value, link }) => (
    <div className="flex items-start gap-3">
        {icon && <div className="mt-0.5 text-gray-400">{icon}</div>}
        <div className="flex-grow min-w-0">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</div>
            {link ? (
                <Link to={link} className="text-sm text-primary-600 hover:text-primary-700 font-medium hover:underline">
                    {value || "—"}
                </Link>
            ) : (
                <div className="text-sm text-gray-900 font-medium break-words">{value || "—"}</div>
            )}
        </div>
    </div>
);

const StatCard = ({ icon, label, value, color = "primary" }) => (
    <div className={`bg-gradient-to-br from-${color}-50 to-${color}-100 rounded-lg p-4 border border-${color}-200`}>
        <div className="flex items-center justify-between">
            <div>
                <div className={`text-2xl font-bold text-${color}-700`}>{value || 0}</div>
                <div className="text-xs font-medium text-gray-600 mt-1">{label}</div>
            </div>
            <div className={`text-${color}-600 opacity-80`}>{icon}</div>
        </div>
    </div>
);


const UserDetailsPage = () => {
    const { t } = useTranslation();
    const { userId } = useParams();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        setLoading(true);
        API.get(`/users/${userId}/details`)
            .then((res) => {
                setData(res.data);
                setError("");
            })
            .catch((err) => {
                setError(apiErrorMessage(err, t("pages.userDetails.loadFailed")));
            })
            .finally(() => setLoading(false));
    }, [userId]);

    const user = data?.user;
    const address = data?.address;
    const addressLines = formatAddressLines(address);
    const fullName = [user?.firstName, user?.middleName, user?.lastName].filter(Boolean).join(" ");
    const initials = `${user?.firstName?.charAt(0) || ''}${user?.lastName?.charAt(0) || ''}`;

    if (loading) {
        return (
            <PageLoadingShell
                variant="page-shell"
                title={t("pages.userDetails.title")}
                subtitle={t("pages.userDetails.accountInfo")}
            />
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
                <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">{t("pages.userDetails.loadFailed")}</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <PageBackSection />
                </div>
            </div>
        );
    }

    if (!data || !user) return null;

    const primaryRole = data.roles?.[0];
    const viewerRole = getActiveRole();
    const editPath = userEditPath(user.id, data.roles, viewerRole);
    const studentsListPath = userListPath("STUDENT", viewerRole);
    const coachesListPath = userListPath("COACH", viewerRole);
    const managersListPath = userListPath("MANAGER", viewerRole);

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="w-full space-y-6 p-4">
                <PageHeaderSection
                    eyebrow={t("pages.userDetails.title")}
                    title={fullName || t("pages.userDetails.title")}
                    subtitle={user.username ? `@${user.username}` : undefined}
                    aside={editPath ? (
                        <Link
                            to={editPath}
                            className="btn-page-action-secondary"
                        >
                            {t("pages.userDetails.editUser")} →
                        </Link>
                    ) : null}
                />

                {/* Header Card */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6 mt-4">
                    <div className={`h-32 ${ROLE_COLORS[primaryRole]?.replace('text-white', '') || 'bg-gradient-to-r from-gray-400 to-gray-500'}`}></div>
                    <div className="px-6 pb-6">
                        <div className="flex flex-col sm:flex-row items-start sm:items-end -mt-16 sm:-mt-12">
                            <div className="relative">
                                <div className="w-24 h-24 sm:w-28 sm:h-28 bg-white rounded-2xl shadow-xl flex items-center justify-center text-gray-700 text-3xl sm:text-4xl font-bold border-4 border-white">
                                    {initials}
                                </div>
                                <div className={`absolute -bottom-1 -right-1 w-8 h-8 rounded-full ${user.active ? 'bg-green-500' : 'bg-gray-400'} border-4 border-white`}></div>
                            </div>

                            <div className="mt-4 sm:mt-0 sm:ml-6 flex-grow">
                                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">{fullName}</h1>
                                <p className="text-gray-500 mt-1">@{user.username}</p>

                                {data.roles?.length > 0 && (
                                    <div className="flex gap-2 mt-3 flex-wrap">
                                        {data.roles.map((role) => (
                                            <span
                                                key={role}
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold shadow-sm ${ROLE_COLORS[role] || "bg-gray-200 text-gray-800"}`}
                                            >
                                                {ROLE_ICONS[role]}
                                                {t(`faq.roles.${role}`, { defaultValue: role })}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Main Info */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Contact Information */}
                        <InfoCard
                            title={t("pages.userDetails.contactInfo")}
                            icon={
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            }
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <InfoField
                                    icon={
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                        </svg>
                                    }
                                    label={t("common.email")}
                                    value={user.email}
                                />
                                <InfoField
                                    icon={
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                        </svg>
                                    }
                                    label={t("common.phone")}
                                    value={user.phoneNumber}
                                />
                            </div>
                        </InfoCard>

                        {/* Personal Details */}
                        <InfoCard
                            title={t("pages.userDetails.personalInfo")}
                            icon={
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            }
                        >
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <InfoField
                                    icon={
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                        </svg>
                                    }
                                    label={t("pages.userDetails.dateOfBirth")}
                                    value={formatDate(user.dob)}
                                />
                                <InfoField
                                    icon={
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                        </svg>
                                    }
                                    label={t("pages.userDetails.gender")}
                                    value={
                                        user.gender === 'MALE' ? t('pages.userDetails.genderMale')
                                            : user.gender === 'FEMALE' ? t('pages.userDetails.genderFemale')
                                                : user.gender === 'OTHER' ? t('pages.userDetails.genderOther')
                                                    : (user.gender || '—')
                                    }
                                />
                                <InfoField
                                    icon={
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                                        </svg>
                                    }
                                    label={t("common.status")}
                                    value={
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                            {user.active ? `● ${t("common.active")}` : `○ ${t("common.inactive", { defaultValue: "Inactive" })}`}
                                        </span>
                                    }
                                />
                            </div>
                        </InfoCard>

                        {/* Address — hidden when optional fields were left blank (country-only pre-fill) */}
                        {hasAnyAddress(address) && (
                            <InfoCard
                                title={t("pages.userDetails.address")}
                                icon={
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                }
                            >
                                <div className="text-gray-700 leading-relaxed">
                                    {addressLines.map((line, index) => (
                                        <p
                                            key={index}
                                            className={
                                                index === 0
                                                    ? "font-medium"
                                                    : index === addressLines.length - 1
                                                      ? "text-gray-500"
                                                      : ""
                                            }
                                        >
                                            {line}
                                        </p>
                                    ))}
                                </div>
                            </InfoCard>
                        )}

                        {data.roles?.includes("STUDENT") && user?.id && (
                            <GuardiansSection studentId={user.id} />
                        )}

                        {/* Role-specific sections */}
                        {data.studentEnrollments && data.studentEnrollments.length > 0 && (
                            <InfoCard
                                title={t("pages.userDetails.activeEnrollments")}
                                icon={
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                                    </svg>
                                }
                            >
                                <div className="space-y-4">
                                    {data.studentEnrollments.map((enrollment, index) => (
                                        <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                            <div className="space-y-3">
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    <div>
                                                        <div className="text-xs text-gray-500 mb-1">{t("enrollments.form.level")}</div>
                                                        <div className="font-medium text-gray-900 text-sm">{enrollment.level}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs text-gray-500 mb-1">{t("pages.enrollmentDetail.enrolledDate")}</div>
                                                        <div className="font-medium text-gray-900 text-sm">{formatDate(enrollment.enrolledOn)}</div>
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-gray-500 mb-1">{t("enrollments.form.trainingBatch")}</div>
                                                    <div className="font-medium text-gray-900 text-sm break-words">{enrollment.batch}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </InfoCard>
                        )}
                    </div>

                    {/* Right Column - Stats & Quick Info */}
                    <div className="space-y-6">
                        {/* System Information */}
                        <InfoCard
                            title={t("pages.userDetails.accountInfo")}
                            icon={
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            }
                        >
                            <InfoField
                                icon={
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                    </svg>
                                }
                                label={t("pages.userDetails.userId")}
                                value={`#${user.id}`}
                            />
                            <InfoField
                                icon={
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                    </svg>
                                }
                                label={t("pages.userDetails.memberSince")}
                                value={formatDate(user.createdAt)}
                            />
                            <InfoField
                                icon={
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                    </svg>
                                }
                                label={t("pages.studentImport.lastUpdated")}
                                value={formatDate(user.updatedAt)}
                            />
                        </InfoCard>

                        {/* Coach Stats */}
                        {data.coachDetails && (
                            <div className="space-y-3">
                                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                    <svg className="w-5 h-5 text-warning-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                                    </svg>
                                    {t("pages.userDetails.coachSummary")}
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <StatCard
                                        icon={
                                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                                            </svg>
                                        }
                                        label={t("search.students")}
                                        value={data.coachDetails.totalStudents}
                                        color="success"
                                    />
                                    <StatCard
                                        icon={
                                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                                            </svg>
                                        }
                                        label={t("search.batches")}
                                        value={data.coachDetails.assignedBatches}
                                        color="primary"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Quick Actions */}
                        <InfoCard title={t("common.actions")} icon={
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                        }>
                            <div className="space-y-2">
                                {editPath && (
                                    <Link
                                        to={editPath}
                                        className="block px-4 py-2 bg-white border border-primary-200 hover:bg-primary-50 text-primary-700 rounded-lg transition-colors font-medium"
                                    >
                                        {t("pages.userDetails.editUser")} →
                                    </Link>
                                )}
                                {data.roles?.includes('STUDENT') && studentsListPath && (
                                    <Link to={studentsListPath} className="block px-4 py-2 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-lg transition-colors">
                                        {t("pages.studentBulkImport.viewAllStudents")} →
                                    </Link>
                                )}
                                {data.roles?.includes('COACH') && coachesListPath && (
                                    <Link to={coachesListPath} className="block px-4 py-2 bg-warning-50 hover:bg-warning-100 text-warning-700 rounded-lg transition-colors">
                                        {t("nav.coaches")} →
                                    </Link>
                                )}
                                {data.roles?.includes('MANAGER') && managersListPath && (
                                    <Link to={managersListPath} className="block px-4 py-2 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-lg transition-colors">
                                        {t("nav.managers")} →
                                    </Link>
                                )}
                            </div>
                        </InfoCard>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDetailsPage;

